import { useEffect, useMemo } from 'react'
import type { FormInstance } from 'antd/es/form/Form'
import { getTariffValueMin } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/SelectTariffValue/SelectTariffValue'
import { getTotalPrice } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/utils/tariff'
import { parseAndValidateAddedCourses } from '../SelectAdditionalCourses/utils/parseAddedCourses'
import { isStreamCustomPeriodTariff } from '../../tariffTypes'
import {
  discountModeToApplyEachPayment,
  resolveCrmLinkDiscountMode,
  type CrmLinkDiscountMode,
} from '../../discountMode.utils'
import {
  getStreamCustomPeriodBasePrice,
  getStreamCustomPeriodSuggestedPrice,
  getStreamPackageMainNet,
  getStreamPeriodToggleResetValues,
  resolveStreamPeriodValue,
} from './streamCustomPeriodLink.utils'

type UseStreamCustomPeriodLinkFormParams = {
  form: FormInstance
  typeWatch: string | null | undefined
  selectedTariff?: ICourseTariff | null
  valueWatch: number | null
  discountWatch: number | null
  discountModeWatch: CrmLinkDiscountMode | null
  applyDiscountEachPaymentWatch: boolean
  priceWatch: number | null
  additionalCoursesWatch: string | null
  courses?: readonly { id?: number | null }[] | null
  extraCoursesDiscountsRaw?: string | null
  extraCoursesDiscountUnit?: string | null
  isPeriodFixed: boolean
  setIsPeriodFixed: (value: boolean) => void
}

/**
 * Оркестрация stream_custom_period/days/weeks в create-link:
 * value sync, price с discount_mode, toggle фиксации.
 */
export const useStreamCustomPeriodLinkForm = ({
  form,
  typeWatch,
  selectedTariff,
  valueWatch,
  discountWatch,
  discountModeWatch,
  applyDiscountEachPaymentWatch,
  priceWatch,
  additionalCoursesWatch,
  courses,
  extraCoursesDiscountsRaw,
  extraCoursesDiscountUnit,
  isPeriodFixed,
  setIsPeriodFixed,
}: UseStreamCustomPeriodLinkFormParams) => {
  const isActive =
    typeWatch === 'course' && isStreamCustomPeriodTariff(selectedTariff?.type)

  const periodValue = resolveStreamPeriodValue(
    valueWatch,
    selectedTariff,
    isActive,
  )

  const basePrice = getStreamCustomPeriodBasePrice(selectedTariff, periodValue)

  const resolvedDiscountMode = resolveCrmLinkDiscountMode({
    discount: discountWatch,
    discountMode: discountModeWatch,
    applyDiscountEachPayment: applyDiscountEachPaymentWatch,
    isPeriodFixed,
  })

  const suggestedPrice = useMemo(
    () =>
      getStreamCustomPeriodSuggestedPrice(
        selectedTariff,
        periodValue,
        discountWatch,
        discountModeWatch,
        applyDiscountEachPaymentWatch,
        isPeriodFixed,
      ),
    [
      applyDiscountEachPaymentWatch,
      discountModeWatch,
      discountWatch,
      isPeriodFixed,
      periodValue,
      selectedTariff,
    ],
  )

  useEffect(() => {
    if (!isActive || !selectedTariff) return

    if (valueWatch == null) {
      return
    }

    const nextValue =
      Number(valueWatch) >= 1
        ? Math.trunc(Number(valueWatch))
        : getTariffValueMin(selectedTariff) || 1

    if (Number(valueWatch) !== nextValue) {
      form.setFieldValue('value', nextValue)
    }
  }, [form, isActive, selectedTariff?.id, valueWatch])

  useEffect(() => {
    if (!isActive || isPeriodFixed) return
    if (
      suggestedPrice <= 0 &&
      Number(form.getFieldValue('price')) > 0
    ) {
      return
    }
    form.setFieldValue('price', suggestedPrice)
  }, [form, isActive, isPeriodFixed, suggestedPrice])

  const packageTotal = useMemo(() => {
    if (!isActive || !selectedTariff) return 0

    const periodForTotal = periodValue
    if (!periodForTotal) return 0

    const additionalCourses = parseAndValidateAddedCourses(
      additionalCoursesWatch ?? null,
    ).map((item) => ({
      ...item,
      course:
        (courses?.find((course) => course.id === item.courseId) as
          | ICourse
          | null
          | undefined) ?? null,
      withDemo: false,
    }))

    let extraCoursesDiscounts: number[] = []
    try {
      extraCoursesDiscounts =
        JSON.parse(extraCoursesDiscountsRaw ?? 'null') ?? []
    } catch (error) {}

    const coursesWithFixedPrice = additionalCourses.filter(
      (item) => item.price !== null && item.price !== undefined,
    )
    const coursesForDefaultCalculation = additionalCourses.filter(
      (item) => item.price === null || item.price === undefined,
    )
    const fixedAdditionalCoursesPrice = coursesWithFixedPrice.reduce(
      (sum, item) => {
        const price = Number(item.price)
        return sum + (Number.isFinite(price) ? price : 0)
      },
      0,
    )

    return (
      getStreamPackageMainNet(priceWatch, suggestedPrice) +
      fixedAdditionalCoursesPrice +
      getTotalPrice(
        selectedTariff,
        periodForTotal,
        undefined,
        true,
        coursesForDefaultCalculation as any,
        extraCoursesDiscounts,
        extraCoursesDiscountUnit ?? 'percent',
      )
    )
  }, [
    additionalCoursesWatch,
    courses,
    extraCoursesDiscountUnit,
    extraCoursesDiscountsRaw,
    isActive,
    periodValue,
    priceWatch,
    selectedTariff,
    suggestedPrice,
  ])

  const onTogglePeriod = (checked?: boolean) => {
    const isChecked =
      typeof checked === 'boolean' ? checked : !isPeriodFixed

    setIsPeriodFixed(isChecked)

    const hasDiscount = Number(discountWatch) > 0
    const nextMode: CrmLinkDiscountMode | null = hasDiscount
      ? isChecked
        ? 'renewal_only'
        : 'first_only'
      : null

    form.setFieldsValue({
      ...getStreamPeriodToggleResetValues(selectedTariff, valueWatch),
      discount_mode: nextMode,
      apply_discount_each_payment: discountModeToApplyEachPayment(nextMode),
      ...(isChecked
        ? {
            price: getStreamCustomPeriodSuggestedPrice(
              selectedTariff,
              resolveStreamPeriodValue(valueWatch, selectedTariff, true),
              discountWatch,
              nextMode,
              false,
              true,
            ),
          }
        : {}),
    })
  }

  const onDiscountModeChange = (mode: CrmLinkDiscountMode | null) => {
    const nextMode = resolveCrmLinkDiscountMode({
      discount: discountWatch,
      discountMode: mode,
      isPeriodFixed,
    })
    form.setFieldsValue({
      discount_mode: nextMode,
      apply_discount_each_payment: discountModeToApplyEachPayment(nextMode),
    })
  }

  return {
    isActive,
    isPeriodFixed,
    periodValue,
    basePrice,
    suggestedPrice,
    packageTotal,
    discountMode: resolvedDiscountMode,
    onTogglePeriod,
    onDiscountModeChange,
  }
}
